import { useCallback, useEffect, useMemo, useRef } from "react";
import { synthesizeSpeech } from "../api/ttsClient";
import type { TtsPlayer } from "../audio/ttsPlayer";
import { getTtsPlayer } from "../audio/ttsPlayerInstance";
import { VadController } from "../audio/vadController";
import {
  recognizeSingleUtterance,
  type MicHandle,
  type SegmentListener,
} from "../audio/singleUtteranceRecognizer";
import { parseYesNo, type YesNo } from "../audio/yesNoIntent";
import { AppError } from "../errors/types";

// 事前チェックの音声案内 (spec 2026-07-19 §5.1)。wizard 全体で TtsPlayer を
// 1 インスタンスだけ持ち、読み上げ・聴取・割り込みをここに集約する。
// プレイヤーが 1 つなので出力デバイス (sinkId) の適用箇所も 1 つで済む。
// 聴取中に UI へ返すイベント (課題6/Approach A)。onSpeechStart は D18 の barge-in
// 通知も兼ねる。認識の挙動は変えず、呼び出し側 (NameCapture) が「聞き取っています…」
// →「確認しています…」と状態表示を進めるために使う。
// onSpeechEnd の引数は認識器が渡す 16 kHz mono の PCM (spec 2026-07-29 §4.1)。氏名の発話を
// R2 に保存するのに使うので、ここで落とすと保存する音声そのものが手に入らない。
// 引数を無視するコールバック (状態表示の前倒しだけをする既存の呼び出し) はそのまま通る。
// onNarrated は「この聴取に添えた読み上げがもう鳴らない」通知 (2026-08-20)。
// **鳴り終わった場合だけでなく、合成に失敗した場合・割り込みで切れた場合も呼ぶ** —
// 呼び出し側 (NameCapture) はこれを待って「はい」を押せるようにするので、
// 呼ばない経路が 1 つでもあると本人が確定できなくなる (原則 5 違反)。
// text を渡さない聴取では同期的に 1 度呼ぶ (読み上げが無い = 最初から鳴り終わっている)。
//
// **必ず高々 1 回**。呼び出し側が「読み終わった」を state に持つので、
// 2 度目が来ると 1 度目の後の割り込みを取り消してしまう。
export type ListenCallbacks = {
  onSpeechStart?: () => void;
  onSpeechEnd?: (audio: Float32Array) => void;
  onNarrated?: (elapsedMs: number) => void;
};

export type PreflightVoice = {
  speak: (text: string) => Promise<void>;
  speakOnce: (text: string) => Promise<void>;
  stopSpeaking: () => void;
  // callbacks は askText と同じ形にしてある (2026-08-20)。それまでは
  // `onSpeechStart?: () => void` を直に取っていたが、**production から 1 箇所も
  // 渡されていなかった** (D20 で SpeakerCheck が使っていた経路は D25 で聴取ごと廃止)。
  // onNarrated を通すために形を揃えた。
  askYesNo: (text?: string, callbacks?: ListenCallbacks) => Promise<YesNo>;
  askText: (text?: string, callbacks?: ListenCallbacks) => Promise<string>;
  cancel: () => void;
};

// 落としたセグメントの記録 (spec 2026-08-22 D9)。**この hook は emit しない** —
// 誰も所有していないセグメントを見つけるのはここだが、イベントの型・上限・
// sessionId は Preflight の持ち物なので、そこへ渡すだけにする (原則 4)。
export type DroppedSegment = {
  // **セグメントが開いた瞬間に TTS が鳴っていたか。** 多ければ AEC が効いていない
  // = 読み上げの回り込みでバッファが空にされている (= (A) の残差)。
  // **閉じる頃には分からない** — onSpeechRealStart が既に読み上げを止めている。
  speaking: boolean;
  durationMs: number;
  // セグメントが開いた時刻 − 直近の聴取の境界。**負なら「その聴取が始まる前から
  // 話していた」** = 早すぎる発話を捨てている (spec §3.3 の最後)。
  sinceListenMs: number;
};

type Opts = {
  stream: MediaStream | null;
  outputDeviceId?: string;
  // 氏名 step にいる間だけ true (spec 2026-08-22 D2)。VAD の寿命をこの旗に合わせる。
  // **開きっぱなしにしてはいけない** — チュートリアルと完了案内は「読み上げるだけで
  // 人が話さない」step なので vad.* が撒かれると正体不明の発話がログに並び、
  // さらに本編が自分の VAD を作るので同じ MediaStream を 2 本が解析することになる。
  micOpen?: boolean;
  onSegmentDropped?: (info: DroppedSegment) => void;
};

// 短い案内文の合成なので数秒で十分 (アンケート回答本文の合成とは違う想定、Fix 5)。
const SYNTHESIS_TIMEOUT_MS = 5000;

// synthesizeSpeech() 自体には timeout がなく、fetch がハングする (キャプティブ
// ポータル/VPN 切断等) と speak()/speakOnce() の await が無期限に止まる。事前
// チェックは全 step が await voice.speak(...)/speakOnce(...) を制御フローの
// 一部として直列に使うため、1 箇所のハングが以降の案内・自動リスニング・
// auto-advance をすべて止めてしまう (NameCapture の失敗リトライ、ready step の
// 完了案内など)。ttsClient の signature は変えず、ここで Promise.race 相当に
// 包んで有限時間に収める。速い方 (合成 or timeout) が勝つだけで、synthesizeSpeech
// 自体の fetch を abort するわけではない (ttsClient は変更しない制約のため)。
function synthesizeWithTimeout(
  text: string
): Promise<{ audio: Blob; durationMs: number; model: string }> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new AppError("tts-failed", "読み上げの合成がタイムアウトしました。"));
    }, SYNTHESIS_TIMEOUT_MS);
    synthesizeSpeech(text).then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

export function usePreflightVoice({
  stream,
  outputDeviceId,
  micOpen = false,
  onSegmentDropped,
}: Opts): PreflightVoice {
  // 不変条件 (spec §5.5): 同時に生きている recognizer は高々 1 つ。
  // 新しい聴取の前に必ず前を abort する (AudioContext の枯渇対策)。
  const abortRef = useRef<AbortController | null>(null);
  // 読み上げの世代カウンタ。cancel()/stopSpeaking() で 1 つ進める。speak()/speakOnce() は
  // 合成開始時点の世代を覚えておき、合成完了時に世代がずれていたら (その間に
  // cancel/stopSpeaking された) 再生を開始しない。unmount 後や step 遷移後に、遅れて
  // 届いた合成結果が再生されてしまう事故を防ぐ (レビュー指摘の反映、2026-07-19)。
  const generationRef = useRef(0);
  // prop は毎レンダー最新値へ同期する ref に鏡写しする。コールバックを
  // useCallback で安定させたまま最新値を読むため (stale closure 対策)。
  const streamRef = useRef(stream);
  streamRef.current = stream;
  const outputDeviceIdRef = useRef(outputDeviceId);
  outputDeviceIdRef.current = outputDeviceId;

  // ===== マイク (VAD) の持ち主 (spec 2026-08-22 D1 / D2) =====
  //
  // **氏名 step のあいだ 1 本だけ生かす。** 1 発話ごとに作り直すと、プリロールの
  // 環リングバッファ (構造上の上限 480ms) が**毎回空から始まる**。言い直しは案内が短く
  // 本人も身構えているのですぐ答えるので、起動待ちがそのまま応答窓に落ちて録音の頭が欠ける
  // (実測 11/46。言い直しでの発生率は 1 回目の 4.5 倍 = 2/24 対 9/22、spec §1)。
  // **本編 (useContinuousMode) は既にこの形で 1 か月動いている** — 新しい形の発明ではない。
  const vadRef = useRef<VadController | null>(null);
  // 起動中のもの。destroyMic() がこれを null にすることで「起動中に壊された」が伝わる。
  const pendingVadRef = useRef<VadController | null>(null);
  const startingRef = useRef<Promise<void> | null>(null);
  // いま聴取している受け手 (attach で入り detach で外れる)。
  const listenerRef = useRef<SegmentListener | null>(null);
  // **いま開いているセグメントの持ち主** (D3)。開いた瞬間の listener で決まる。
  const ownerRef = useRef<SegmentListener | null>(null);
  const segmentOpenedAtRef = useRef(0);
  const segmentSpeakingRef = useRef(false);
  const listenOpenedAtRef = useRef<number | null>(null);
  const listenClosedAtRef = useRef<number | null>(null);
  const onSegmentDroppedRef = useRef(onSegmentDropped);
  onSegmentDroppedRef.current = onSegmentDropped;

  const destroyMic = useCallback(() => {
    startingRef.current = null;
    const pending = pendingVadRef.current;
    pendingVadRef.current = null;
    pending?.destroy();
    const live = vadRef.current;
    vadRef.current = null;
    if (live !== pending) live?.destroy();
    ownerRef.current = null;
  }, []);

  const ensureMic = useCallback(async (): Promise<void> => {
    if (vadRef.current?.isRunning()) return;
    if (startingRef.current) return startingRef.current;
    const s = streamRef.current;
    if (!s) throw new AppError("stt-failed", "マイクが利用できません。");

    // **同一性の比較でなければならない** (D3)。真偽値の旗にすると、「A が attach 中に
    // セグメントが開き、A が detach して B が attach し、そのセグメントが終わる」経路で
    // **B が A のセグメントを受け取る**。氏名の聴取と復唱確認は連続して起きるので、
    // この経路は日常的に踏まれる。
    const owned = () => ownerRef.current !== null && ownerRef.current === listenerRef.current;

    // onError は張らない — VadController が呼ぶのは start() の catch からだけなので、
    // ensure() の reject と同じ経路になる (行き先は registerFailure → 3 回でキーボード)。
    const controller = new VadController({
      onSpeechStart: () => {
        // 所有者は**開いた瞬間**に決まる。null かもしれない = 誰のものでもない。
        ownerRef.current = listenerRef.current;
        segmentOpenedAtRef.current = Date.now();
        // **鳴っているかはここでしか分からない** — セグメントが閉じる頃には
        // onSpeechRealStart が読み上げを止めている (spec §11.1)。
        segmentSpeakingRef.current = getTtsPlayer().isPlaying();
        if (owned()) listenerRef.current?.onSpeechStart?.();
      },
      // **D4。ここに所有権を掛けないと、前の聴取で開いたセグメントが次の listen() の
      // 読み上げを切る** — 2026-08-20 で塞いだ「他人の音で読み上げが切れる」の再生産で、
      // しかも今回は「はい」の gate が付いているので**読み終わる前に押せてしまう**
      // (TtsPlayer.stop() は保留中の play() を resolve するため)。
      onSpeechRealStart: () => {
        if (owned()) listenerRef.current?.onSpeechRealStart?.();
      },
      onSpeechEnd: (audio) => {
        const mine = owned();
        ownerRef.current = null;
        if (mine) {
          listenerRef.current?.onSpeechEnd?.(audio);
          return;
        }
        // 誰のものでもないセグメント。**WAV は保存しない** (D10 — preflight.name_audio の
        // 通番がずれると段 3 の index 突き合わせが壊れる)。長さと文脈だけを残す。
        const base =
          listenerRef.current !== null ? listenOpenedAtRef.current : listenClosedAtRef.current;
        onSegmentDroppedRef.current?.({
          speaking: segmentSpeakingRef.current,
          durationMs: Math.round(audio.length / 16), // 16 kHz mono
          sinceListenMs: base === null ? 0 : Math.round(segmentOpenedAtRef.current - base),
        });
      },
      onVADMisfire: () => {
        // 200ms に届かなかった音。所有者を戻しておく。
        //
        // **これは等価変異である (2026-08-26 に実測)。** 消してもテストは 1 本も落ちない。
        // vad-web の `frame-processor.js` は `speaking` を厳密な 2 状態で回しており、
        // `SpeechStart` は `!speaking → speaking` でしか鳴らず、`SpeechEnd` と
        // `VADMisfire` は `speaking → !speaking` の**どちらか一方だけ**が鳴る。
        // つまり次の `onSpeechEnd` の前に必ず `onSpeechStart` が来て所有者を上書きするので、
        // 古い所有者が読まれる経路が存在しない。
        //
        // **残してあるのは、その前提が vendor 側の実装だからである。** 消すなら
        // `frame-processor.js` の `speaking` の遷移が今も 2 状態であることを確かめること。
        ownerRef.current = null;
      },
    });
    pendingVadRef.current = controller;
    const p = (async () => {
      try {
        await controller.start(s);
        if (pendingVadRef.current !== controller) {
          // 起動中に destroyMic() された (step を出た / 入力デバイスが変わった)
          controller.destroy();
          return;
        }
        vadRef.current = controller;
      } catch (e) {
        // **失敗をキャッシュしない (D5)。** 次の ensure() が作り直す。
        // キャッシュすると氏名 step が丸ごと死ぬ (原則 5 の直接の違反)。
        controller.destroy();
        vadRef.current = null;
        throw e;
      } finally {
        startingRef.current = null;
        if (pendingVadRef.current === controller) pendingVadRef.current = null;
      }
    })();
    startingRef.current = p;
    return p;
  }, []);

  const mic = useMemo<MicHandle>(
    () => ({
      ensure: ensureMic,
      attach: (l) => {
        listenerRef.current = l;
        listenOpenedAtRef.current = Date.now();
        return () => {
          if (listenerRef.current !== l) return;
          listenerRef.current = null;
          listenClosedAtRef.current = Date.now();
        };
      },
    }),
    [ensureMic]
  );

  // **step を出たら必ず止める** (D2)。止め損なうと本編が自分の VAD を作ったときに
  // 同じ MediaStream を 2 本の VAD が解析することになる (2026-08-02 H2 で塞いだ欠陥の
  // 再演で、症状は「1 発話で commit が 2 回走り、同じ発話が 2 つの設問の回答になる」)。
  //
  // **stream を deps に入れているのは入力デバイスの切替のため。** React は新しい effect の
  // 前に古い effect の cleanup を走らせるので、「古い VAD を壊す → 新しい stream で
  // 起動する」の順序が構造的に保証される (逆だと一瞬 2 本になる)。
  useEffect(() => {
    if (!micOpen || !stream) {
      destroyMic();
      return;
    }
    // 起動失敗をここで握るのは、**この effect が「暖めておく」だけの役だから** —
    // 本当に必要になったときは listen() の中の ensure() が同じ失敗を reject として
    // 受け直し、registerFailure に落ちる (原則 5 の出口は 1 ミリも動かない)。
    void ensureMic().catch(() => undefined);
    return destroyMic;
  }, [micOpen, stream, ensureMic, destroyMic]);

  const getPlayer = useCallback((): TtsPlayer => {
    const player = getTtsPlayer();
    if (outputDeviceIdRef.current) {
      // 出力先の適用は fire-and-forget (失敗しても既定出力で鳴る、原則 5)
      void player.setSinkId(outputDeviceIdRef.current).catch(() => undefined);
    }
    return player;
  }, []);

  // 出力デバイスの切替は次に getPlayer() が呼ばれるまで (=次の読み上げが始まるまで)
  // 何も起きない。スピーカー確認はループ再生中に出力先を変えても、既存の
  // <audio>/<oscillator> はそのまま古い出力に鳴り続けてしまう。この画面は
  // 「聞こえない人への唯一の安全弁」(spec §4.2) なので、選択操作そのものが
  // 音を動かせる必要がある。outputDeviceId が変わるたびに、既に生成済みの
  // player があればそこへ直接 setSinkId を当て、再生中の音声をリダイレクトする
  // (Fix 4a)。fire-and-forget (原則 5: 失敗しても既定出力で鳴り続ける、ここで
  // 待ってユーザーをブロックしない)。player 未生成 (まだ何も読み上げていない)
  // ときは何もしない — 次の getPlayer() が生成直後に同じ値を適用する。
  useEffect(() => {
    // outputDeviceId が undefined (= 「既定のデバイス」を選び直した) ときは "" を渡す。
    // TtsPlayer は "" を「既定へ戻す」として扱い (getTtsPlayer().ts の setSinkId / play)、
    // 新規 Audio には sinkId を適用しないので OS 既定で鳴る。ここで undefined を弾くと
    // sinkId が前の値のまま残り、聞こえない人が総当たりで試したときに戻れなくなる (#3)。
    void getTtsPlayer()
      .setSinkId(outputDeviceId ?? "")
      .catch(() => undefined);
  }, [outputDeviceId]);

  // 読み上げ。合成・再生・autoplay ブロックのいずれで失敗しても resolve する:
  // 案内は画面テキストが担うので、ここで throw して呼び出し側の流れを
  // 止めてはならない (原則 5)。
  const speak = useCallback(
    async (text: string): Promise<void> => {
      const generation = generationRef.current;
      try {
        const { audio } = await synthesizeWithTimeout(text);
        if (generation !== generationRef.current) return; // 合成中に cancel/stopSpeaking された
        await getPlayer().play(audio);
      } catch {
        // 意図的に握り潰す (timeout も含む — 合成失敗と同じ quiet-resolve 経路、Fix 5)
      }
    },
    [getPlayer]
  );

  // 1 回だけ再生 (スピーカーテスト用、D20)。stopSpeaking()/cancel()/世代ずれ で resolve し、
  // 合成・再生の失敗は reject する — 呼び出し側 (SpeakerCheck) がこの reject を固定トーン
  // fallback のトリガーとして使うため (D4)。cadence (初回 2 回 → 以降 10 秒ごとに 1 回) は
  // 呼び出し側がこの 1 回再生の反復として組む。旧 speakLoop (連続ループ) は D20 で廃止。
  const speakOnce = useCallback(
    async (text: string): Promise<void> => {
      const generation = generationRef.current;
      // 合成のハングも timeout で reject に倒れる (Fix 5)。speak() と違いここでは
      // 握り潰さない — reject をトーン fallback のトリガーに使うため、genuine な失敗と
      // 同じ経路に乗せる必要がある。
      const { audio } = await synthesizeWithTimeout(text);
      // 合成中に cancel/stopSpeaking されたら再生しない。意図的な中断であり TTS の
      // 失敗ではないので reject せず resolve する (呼び出し側のトーン fallback を
      // 誤発火させない)。
      if (generation !== generationRef.current) return;
      await getPlayer().play(audio);
    },
    [getPlayer]
  );

  const stopSpeaking = useCallback(() => {
    generationRef.current += 1;
    getTtsPlayer().stop();
  }, []);

  const cancel = useCallback(() => {
    generationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    getTtsPlayer().stop();
  }, []);

  // 読み上げと聴取を同時に走らせ、発話検出で読み上げを止める (D18)。
  // text を省略すると聴取のみ (スピーカーテストのようにループ再生が別途
  // 走っている場面で使う)。
  const listen = useCallback(
    async (text?: string, callbacks?: ListenCallbacks): Promise<string> => {
      // stream が null で早期 throw する経路でも不変条件 (recognizer は高々 1 つ) を
      // 保つため、null 判定より前に必ず前の聴取を abort する (後回しにすると、abort
      // されないまま前の recognizer が生き残ってしまう)。
      abortRef.current?.abort();
      const s = streamRef.current;
      if (!s) throw new AppError("stt-failed", "マイクが利用できません。");
      const controller = new AbortController();
      abortRef.current = controller;
      const player = getPlayer();
      // 合成には数百 ms かかる。その間に割り込まれたら「遅れて届いた音声」を
      // 鳴らしてはならない (ユーザーの発話に被せてしまう) ため、interrupted で
      // 再生自体を捨てる。
      let interrupted = false;
      // onNarrated を高々 1 回に絞る (成功・失敗・割り込みのどの経路からも呼ぶため)。
      const startedAt = Date.now();
      let narratedNotified = false;
      const notifyNarrated = () => {
        if (narratedNotified) return;
        narratedNotified = true;
        callbacks?.onNarrated?.(Date.now() - startedAt);
      };
      if (text !== undefined) {
        void (async () => {
          try {
            const { audio } = await synthesizeSpeech(text);
            if (interrupted || controller.signal.aborted) return;
            await player.play(audio);
          } catch {
            // 案内は画面テキストが担う (原則 5)
          } finally {
            // **finally に置くのが要点。** 合成の失敗・autoplay ブロック・
            // 割り込みによる早期 return のいずれでも「もう鳴らない」ことは確定するので、
            // ここを通らない経路を作ってはいけない (通らないと「はい」が永久に押せない)。
            notifyNarrated();
          }
        })();
      } else {
        notifyNarrated(); // 読み上げが無い聴取は最初から鳴り終わっている
      }
      try {
        return await recognizeSingleUtterance({
          mic,
          signal: controller.signal,
          onSpeechStart: () => {
            // **読み上げを止めるのはここではない** (2026-08-20)。この callback は
            // しきい値超えの最初の 1 フレーム (32ms) で発火するので、咳・物音・
            // スピーカーから回り込んだ AI 自身の声でも鳴る。実測で氏名の復唱確認の
            // 26% がこれで切れており、うち 2 件は `vad.misfire` = 発話ですらなかった
            // (計測 2026-08-20)。止める判断は onSpeechRealStart (200ms) が持つ。
            //
            // ここに残すのは UI への通知だけ (課題6: NameCapture が
            // 「聞き取っています…」へ切り替える)。**32ms のままにするのは意図的** —
            // 表示は早いほうがよく、外しても表示が一瞬戻るだけで害が無い。
            callbacks?.onSpeechStart?.();
          },
          // 発話が 200ms 続いたら読み上げを止める (spec 2026-07-28 D11 と同じ判断を
          // 事前チェックにも入れた)。`onVADMisfire` とは排他なので、物音で切れる経路が
          // 構造的に消える。
          onSpeechRealStart: () => {
            interrupted = true;
            player.stop();
          },
          // 発話終了を素通し (課題6: NameCapture が「確認しています…」へ前倒しするのに使う /
          // 2026-07-29: 同じ PCM を氏名音声として R2 に保存する)。**引数も素通しする** —
          // ここで落とすと保存する音声が手に入らない。
          onSpeechEnd: (audio) => callbacks?.onSpeechEnd?.(audio),
        });
      } catch (e) {
        // **D5b: 起動後に死んだ VAD を誰も検出できない。** VadController.isRunning() は
        // `vad !== null` しか見ず、onError は start() の catch からしか呼ばれない。
        // 1 発話ごとに作り直していた頃は**作り直しそのものが自己修復**だったので、
        // 持ち回すなら失敗の契機で作り直す必要がある (でないと以後の聴取が
        // すべて 20 秒 × 3 の沈黙になり、原則 5 の実質的な違反になる)。
        //
        // **中断は失敗ではない。** 次の listen() が始まっただけなので、ここで作り直すと
        // 1 発話ごとの再構築に戻り、この変更の目的がまるごと消える。
        if (!controller.signal.aborted) {
          destroyMic();
          // **すぐ張り直すのが要点。** 次の listen() まで待つとその時点から起動が
          // 始まってプリロールがまた空になる。失敗の後には必ず案内の読み上げが入る
          // (nameRetry ≈ 2 秒 > 480ms) ので、その間に暖まり直せる。
          void ensureMic().catch(() => undefined);
        }
        throw e;
      } finally {
        interrupted = true;
        player.stop();
      }
    },
    [getPlayer, mic, ensureMic, destroyMic]
  );

  const askText = listen;

  const askYesNo = useCallback(
    async (text?: string, callbacks?: ListenCallbacks): Promise<YesNo> =>
      parseYesNo(await listen(text, callbacks)),
    [listen]
  );

  // unmount で読み上げも聴取も止める (mic stream は singleton の持ち物なので触らない)。
  // cancel() は世代カウンタも進めるため、unmount 時点で合成中だった speak/speakOnce の
  // 結果 (合成 fetch 自体は中断しないが) も、後から届いて再生されることはない。
  useEffect(() => cancel, [cancel]);
  // VAD は micOpen の effect が閉じるが、**unmount で micOpen が true のまま消える経路**
  // (本編へ遷移して Preflight ごと畳まれる) があるので、ここでも必ず壊す。
  useEffect(() => destroyMic, [destroyMic]);

  // 返り値オブジェクトを memo 化して参照を安定させる。中身の各関数は元から
  // useCallback で安定しているが、包むオブジェクト自体は毎レンダー新規の
  // オブジェクトリテラルだったため、呼び出し側 (SpeakerCheck 等) が [voice] を
  // effect の deps に使うと親の再レンダーのたびに effect が再発火し、テスト音の
  // ループ・聴取が丸ごと再起動してしまっていた (レビュー指摘の反映、2026-07-20)。
  return useMemo(
    () => ({ speak, speakOnce, stopSpeaking, askYesNo, askText, cancel }),
    [speak, speakOnce, stopSpeaking, askYesNo, askText, cancel]
  );
}
